/**
 * Hooks de gestión de tenants — Super Admin
 * Casos de uso: TEN-01, TEN-02
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import type { Tenant } from '@/types'

export const tenantKeys = {
  all: ['platform', 'tenants'] as const,
}

interface TenantsResponse {
  tenants: Tenant[]
  count: number
}

interface ProvisionPayload {
  name: string
  schema_name: string
  country: string
  tax_id?: string
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
  account_type?: 'production' | 'trial' | 'demo' | 'nfr'
  /**
   * Vigencia de una cuenta que caduca, en días calendario. Demo y trial tienen
   * plazo por defecto; para un NFR es obligatorio, porque caduca cuando termina
   * el contrato de partner y no hay default que inventar.
   */
  trial_days?: number
}

// ─── TEN-02: Listar tenants ───────────────────────────────────────────────────
export function useTenants(filters?: { status?: string; country?: string }) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.country) params.set('country', filters.country)

  return useQuery({
    queryKey: [...tenantKeys.all, filters],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantsResponse>(
        `/platform/tenants/?${params.toString()}`
      )
      return data
    },
  })
}

// ─── TEN-01: Aprovisionar tenant ──────────────────────────────────────────────
export function useProvisionTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ProvisionPayload) => {
      const { data } = await apiClient.post('/platform/tenants/', payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })
}

// ─── TEN-02: Activar / Desactivar tenant ──────────────────────────────────────
export function useToggleTenantStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      action,
    }: {
      tenantId: number
      action: 'activate' | 'deactivate'
    }) => {
      const { data } = await apiClient.post(
        `/platform/tenants/${tenantId}/toggle-status/`,
        { action }
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })
}

// ─── TEN-02: Modificar datos del tenant ───────────────────────────────────────
export function useUpdateTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      tenantId,
      ...payload
    }: {
      tenantId: number
      name?: string
      tax_id?: string
      licensed_users?: number
    }) => {
      const { data } = await apiClient.patch(`/platform/tenants/${tenantId}/`, payload)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.all })
    },
  })
}
